import type { HLDWorkspaceSpec } from './hldWorkspaceSpecs';
import type { CustomHLDBrief } from '../../types/hld';

export type CustomHLDWorkspaceInput = Pick<
  HLDWorkspaceSpec,
  'title' | 'difficulty' | 'estimatedTimeMinutes' | 'statement' | 'functionalRequirements' | 'nonFunctionalRequirements' | 'assumptions'
>;

export type CustomHLDWorkspace = HLDWorkspaceSpec & {
  createdAt: number;
  updatedAt: number;
  isCustom: true;
};

export const toCustomHLDWorkspace = (
  input: CustomHLDWorkspaceInput | CustomHLDBrief,
  id: string,
  createdAt = Date.now(),
  updatedAt = Date.now(),
): CustomHLDWorkspace => ({
  ...input,
  id,
  tags: ['Custom design'],
  referenceSolution: genericReview,
  evaluationRubric: genericRubric,
  createdAt,
  updatedAt,
  isCustom: true,
});

const STORAGE_KEY = 'lbu-hld-custom-workspaces:v1';

const genericReview = {
  referenceArchitecture: 'Review the request path, data ownership, scaling strategy, failure behaviour, and the trade-offs made for this specific brief.',
  deepDives: ['Identify the most difficult component and explain how it remains correct at scale.'],
  commonMistakes: ['Introducing components without connecting them to a requirement or a measured bottleneck.'],
};

const genericRubric = [
  { description: 'Requirements and assumptions are explicit.', maxScore: 2 },
  { description: 'The request flow and responsibilities are clear.', maxScore: 3 },
  { description: 'Scale, failure behaviour, and data decisions are addressed.', maxScore: 3 },
  { description: 'Trade-offs and observability are explained.', maxScore: 2 },
];

const readAll = (): CustomHLDWorkspace[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const createCustomHLDWorkspace = (input: CustomHLDWorkspaceInput): CustomHLDWorkspace => {
  const now = Date.now();
  const workspace = toCustomHLDWorkspace(input, `custom-${now}-${Math.random().toString(36).slice(2, 7)}`, now, now);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([workspace, ...readAll()]));
  } catch {
    // The workspace still opens for the current session even if storage is unavailable.
  }
  return workspace;
};

export const getCustomHLDWorkspace = (id: string): CustomHLDWorkspace | null =>
  readAll().find((workspace) => workspace.id === id) ?? null;

export const listCustomHLDWorkspaces = (): CustomHLDWorkspace[] =>
  [...readAll()].sort((left, right) => right.updatedAt - left.updatedAt);

